import axios from 'axios'
import Axhelper from '@/lib/Axhelper'

class UserService2 {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			users: '/api/uzytkownicy/all',
			usersSearch: '/api/uzytkownicy/search',
			usersDetails: '/api/uzytkownicy/details',
			usersDelete: '/api/uzytkownicy',
			usersCreate: '/api/uzytkownicy',
			usersUpdate: '/api/uzytkownicy/updateuseradmin',
			trainersForTraining: '/api/uzytkownicy/trainersfortraining',
			uploadUserFile: '/api/uzytkownicy/uploadfile',
			downloadUserFile: '/api/uzytkownicy/downloadfile',
			deleteUserFile: '/api/uzytkownicy/deletefile',
		}
	}

	async getById(id) {
		try {
			if (!id) {
				return {
					data: null,
					error: 'Invalid ID provided',
				}
			}

			const url = `${this.endpoints.usersDetails}/${encodeURIComponent(id)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: this._normalizeUser(response.data),
				error: null,
			}
		} catch (error) {
			console.error('UserService2.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch record',
			}
		}
	}

	async getWithFilters(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = 'created',
				sortDirection = 'desc',
				filters = {},
			} = options

			const url = this._buildFilteredUrl({ limit, sortColumn, sortDirection, filters })
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: this._normalizeUsers(response.data),
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('UserService2.getWithFilters error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch records',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async delete(id) {
		try {
			if (!id) {
				return {
					success: false,
					error: 'Invalid ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.usersDelete}/${encodeURIComponent(id)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Record deleted successfully',
			}
		} catch (error) {
			console.error('UserService2.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete record',
				message: null,
			}
		}
	}

	async create(data) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.usersCreate, this._buildFormData(data))

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: this._normalizeUser(response.data),
			}
		} catch (error) {
			console.error('UserService2.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create record',
			}
		}
	}

	async getTrainersForTraining(options = {}) {
		try {
			const {
				g_lat,
				g_long,
				requiredCertificationIds = [],
			} = options

			if (g_lat === '' || g_lat == null || g_long === '' || g_long == null) {
				return {
					data: [],
					error: 'Training location coordinates are required',
					totalCount: 0,
					hasMore: false,
				}
			}

			const params = new URLSearchParams({
				g_lat: String(g_lat),
				g_long: String(g_long),
			})

			;(Array.isArray(requiredCertificationIds) ? requiredCertificationIds : [])
				.filter(Boolean)
				.forEach(certificationId => {
					params.append('requiredcertificates[]', String(certificationId))
				})

			const url = `${this.endpoints.trainersForTraining}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const trainers = this._normalizeTrainersForTraining(response.data)

			return {
				data: trainers,
				error: null,
				totalCount: trainers.length,
				hasMore: false,
				headers: response.headers,
			}
		} catch (error) {
			console.error('UserService2.getTrainersForTraining error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch trainers for training',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async update(id, data) {
		try {
			if (!id) {
				return {
					success: false,
					error: 'Invalid ID provided',
				}
			}

			const url = `${this.endpoints.usersUpdate}/${encodeURIComponent(id)}`
			const response = await Axhelper.sendPutAsync(url, this._buildFormData(data, { includeEmptyPassword: false }))

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: this._normalizeUser(response.data),
			}
		} catch (error) {
			console.error('UserService2.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update record',
			}
		}
	}

	async uploadUserFile(userId, file, onUploadProgress) {
		try {
			if (!userId) {
				return { success: false, error: 'Invalid user ID provided', data: null }
			}
			if (!file) {
				return { success: false, error: 'No file provided', data: null }
			}

			const formData = new FormData()
			formData.append('file', file)
			const url = `${this.endpoints.uploadUserFile}/${encodeURIComponent(userId)}`
			const response = await Axhelper.sendUploadAsync(url, formData, onUploadProgress)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return {
				success: true,
				error: null,
				data: this._normalizeUser(response.data),
			}
		} catch (error) {
			console.error('UserService2.uploadUserFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to upload user file',
				data: null,
			}
		}
	}

	async downloadUserFile(userId, fileUuid) {
		try {
			if (!userId || !fileUuid) {
				return { success: false, error: 'Invalid user file reference', blob: null, filename: null }
			}

			const url = `${this.endpoints.downloadUserFile}/${encodeURIComponent(userId)}/${encodeURIComponent(fileUuid)}`
			const response = await this._downloadBlob(url)

			if (response.error) {
				return { success: false, error: response.error, blob: null, filename: null }
			}

			return {
				success: true,
				error: null,
				blob: response.blob,
				filename: this._extractDownloadFilename(response.headers, fileUuid, userId),
			}
		} catch (error) {
			console.error('UserService2.downloadUserFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to download user file',
				blob: null,
				filename: null,
			}
		}
	}

	async deleteUserFile(userId, fileUuid) {
		try {
			if (!userId || !fileUuid) {
				return { success: false, error: 'Invalid user file reference', data: null }
			}

			const url = `${this.endpoints.deleteUserFile}/${encodeURIComponent(userId)}/${encodeURIComponent(fileUuid)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return {
				success: true,
				error: null,
				data: this._normalizeUser(response.data),
			}
		} catch (error) {
			console.error('UserService2.deleteUserFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete user file',
				data: null,
			}
		}
	}

	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})
		const searchText = (filters?.searchText || '').trim()

		if (searchText.length >= 3) {
			return `${this.endpoints.usersSearch}/${encodeURIComponent(searchText)}?${params.toString()}`
		}

		return `${this.endpoints.users}?${params.toString()}`
	}

	_buildFormData(data = {}, options = {}) {
		const params = new URLSearchParams()
		const includeEmptyPassword = options.includeEmptyPassword ?? true
		const password = data.password ?? ''

		params.append('nazwa', data.nazwa ?? data.name ?? '')
		params.append('alternative_name', data.alternative_name ?? data.alternativeName ?? data.initials ?? '')
		params.append('email', data.email ?? '')
		if (password || includeEmptyPassword) {
			params.append('password', password)
		}
		params.append('isAdmin', String(this._toBoolean(data.isAdmin)))
		params.append('isTrainer', String(this._toBoolean(data.isTrainer)))
		params.append('isCoordinator', String(this._toBoolean(data.isCoordinator)))

		if (this._toBoolean(data.isTrainer)) {
			params.append('tel', data.tel ?? '')
			params.append('miasto', data.miasto ?? '')
			params.append('wojewodztwo', data.wojewodztwo ?? '')
			params.append('kod_pocztowy', data.kod_pocztowy ?? '')
			params.append('ulica', data.ulica ?? '')
			params.append('g_placeid', data.g_placeid ?? '')
			if (data.g_lat !== '' && data.g_lat != null) {
				params.append('g_lat', String(data.g_lat))
			}
			if (data.g_long !== '' && data.g_long != null) {
				params.append('g_long', String(data.g_long))
			}
			params.append('stawka_godzinowa', data.stawka_godzinowa ?? data?.stawki_godzinowe?.U ?? '')
			params.append('stawki_godzinowe', JSON.stringify(data.stawki_godzinowe || {}))
			params.append('prawo_jazdy', JSON.stringify(this._normalizeStringArray(data.prawo_jazdy)))
			params.append('zatrudnienie', this._normalizeEmployment(data.zatrudnienie))
			params.append('RODO', this._normalizeDate(data.RODO))
			params.append('KRK', this._normalizeDate(data.KRK))
			params.append('RSTPS', this._normalizeDate(data.RSTPS))
			params.append('wyksztalcenie', data.wyksztalcenie ?? '')
			params.append('dowod_osobisty', data.dowod_osobisty ?? '')
			params.append('PESEL', data.PESEL ?? '')
			params.append('pelna_teczka', String(this._toBoolean(data.pelna_teczka)))
			params.append('notatka', data.notatka ?? '')
		}

		const certificates = this._buildCertificatesPayload(data)
		if (certificates.length) {
			params.append('certificates', JSON.stringify(certificates))
		}

		return params
	}

	_buildCertificatesPayload(data = {}) {
		return this._normalizeCertificates(data)
	}

	_normalizeCertificates(source = {}) {
		if (Array.isArray(source.certificates)) {
			return source.certificates
				.map(item => ({
					certificateId: `${item?.certificateId ?? item?.certificationId ?? ''}`.trim(),
					certificateName: `${item?.certificateName ?? item?.name ?? item?.nazwa ?? ''}`.trim(),
				}))
				.filter(item => item.certificateId)
		}

		if (Array.isArray(source.certificationIds)) {
			return source.certificationIds
				.filter(Boolean)
				.map(certificateId => ({
					certificateId: String(certificateId),
					certificateName: '',
				}))
		}

		return []
	}

	_normalizeUsers(users) {
		return Array.isArray(users) ? users.map(user => this._normalizeUser(user)) : []
	}

	_normalizeTrainersForTraining(users) {
		return Array.isArray(users)
			? users.map(user => this._normalizeTrainerForTraining(user)).filter(Boolean)
			: []
	}

	_normalizeTrainerForTraining(user) {
		const normalized = this._normalizeUser(user)
		if (!normalized) return null

		const distanceRaw = user?.distanceInKm ?? user?.distanceKm ?? user?.distance ?? user?.odleglosc ?? user?.odległość
		const parsedDistance = distanceRaw === '' || distanceRaw == null ? null : Number(distanceRaw)

		const stawki_godzinowe = normalized.stawki_godzinowe && typeof normalized.stawki_godzinowe === 'object'
			? {
				U: `${normalized.stawki_godzinowe.U ?? ''}`.trim(),
				'D<2': `${normalized.stawki_godzinowe['D<2'] ?? ''}`.trim(),
				'D>2': `${normalized.stawki_godzinowe['D>2'] ?? ''}`.trim(),
				S: `${normalized.stawki_godzinowe.S ?? ''}`.trim(),
			}
			: {
				U: `${normalized.stawka_godzinowa ?? normalized.defaultHourlyRateGross ?? ''}`.trim(),
				'D<2': '',
				'D>2': '',
				S: '',
			}

		return {
			...normalized,
			_id: normalized._id || normalized.id,
			city: `${normalized.miasto ?? normalized.city ?? ''}`.trim(),
			stawki_godzinowe,
			defaultHourlyRateGross: `${stawki_godzinowe.U || normalized.stawka_godzinowa || normalized.defaultHourlyRateGross || ''}`.trim(),
			distanceKm: Number.isFinite(parsedDistance) ? parsedDistance : null,
		}
	}

	_normalizeUser(user) {
		if (!user) return null

		const safeUser = { ...user }
		delete safeUser.pass_hash

		const certificates = this._normalizeCertificates(safeUser)

		const stawkiSource = safeUser.stawki_godzinowe && typeof safeUser.stawki_godzinowe === 'object'
			? safeUser.stawki_godzinowe
			: {}
		const stawki_godzinowe = {
			U: `${stawkiSource.U ?? safeUser.stawka_godzinowa ?? ''}`.trim(),
			'D<2': `${stawkiSource['D<2'] ?? ''}`.trim(),
			'D>2': `${stawkiSource['D>2'] ?? ''}`.trim(),
			S: `${stawkiSource.S ?? ''}`.trim(),
		}

		return {
			...safeUser,
			name: safeUser.nazwa ?? safeUser.name ?? '',
			initials: safeUser.alternative_name ?? safeUser.initials ?? '',
			isAdmin: this._toBoolean(safeUser.isAdmin),
			isTrainer: this._toBoolean(safeUser.isTrainer),
			isCoordinator: this._toBoolean(safeUser.isCoordinator),
			prawo_jazdy: this._normalizeStringArray(safeUser.prawo_jazdy),
			zatrudnienie: this._normalizeEmployment(safeUser.zatrudnienie),
			RODO: this._normalizeDate(safeUser.RODO),
			KRK: this._normalizeDate(safeUser.KRK),
			RSTPS: this._normalizeDate(safeUser.RSTPS),
			wojewodztwo: `${safeUser.wojewodztwo ?? ''}`.trim(),
			wyksztalcenie: `${safeUser.wyksztalcenie ?? ''}`.trim(),
			dowod_osobisty: `${safeUser.dowod_osobisty ?? ''}`.trim(),
			PESEL: `${safeUser.PESEL ?? ''}`.trim(),
			pelna_teczka: this._toBoolean(safeUser.pelna_teczka),
			notatka: `${safeUser.notatka ?? ''}`.trim(),
			pliki: this._normalizeUserFiles(safeUser.pliki),
			stawki_godzinowe,
			stawka_godzinowa: stawki_godzinowe.U || safeUser.stawka_godzinowa || '',
			certificationIds: certificates.map(item => item.certificateId),
			certificates,
		}
	}

	_normalizeUserFiles(files) {
		return (Array.isArray(files) ? files : []).map(item => ({
			fileUuid: `${item?.fileUuid || item?._id || ''}`.trim(),
			filePath: `${item?.filePath || ''}`.trim(),
			originalName: `${item?.originalName || item?.name || ''}`.trim(),
		})).filter(item => item.fileUuid)
	}

	_normalizeEmployment(value) {
		if (Array.isArray(value)) {
			return `${value[0] ?? ''}`.trim()
		}

		if (typeof value === 'string') {
			const trimmed = value.trim()
			if (!trimmed) return ''

			try {
				const parsed = JSON.parse(trimmed)
				if (Array.isArray(parsed)) {
					return `${parsed[0] ?? ''}`.trim()
				}
			} catch {
				// Treat as a single plain value when not valid JSON.
			}

			return trimmed
		}

		return ''
	}

	_normalizeStringArray(value) {
		if (Array.isArray(value)) {
			return value.map(item => `${item ?? ''}`.trim()).filter(Boolean)
		}

		if (typeof value === 'string') {
			const trimmed = value.trim()
			if (!trimmed) return []

			try {
				const parsed = JSON.parse(trimmed)
				if (Array.isArray(parsed)) {
					return parsed.map(item => `${item ?? ''}`.trim()).filter(Boolean)
				}
			} catch {
				// Treat as a single plain value when not valid JSON.
			}

			return [trimmed]
		}

		return []
	}

	_normalizeDate(value) {
		if (value == null || value === '') return ''
		const match = `${value}`.trim().match(/^(\d{4}-\d{2}-\d{2})/)
		return match ? match[1] : ''
	}

	async _downloadBlob(url) {
		try {
			const token = localStorage.getItem('token')
			const response = await axios.get(url, {
				responseType: 'blob',
				withCredentials: true,
				headers: { token },
			})

			return { error: null, blob: response.data, headers: response.headers }
		} catch (error) {
			const errorMessage = error.response?.data || error.message || 'Download failed'
			return { error: errorMessage, blob: null, headers: null }
		}
	}

	_extractDownloadFilename(headers, fileUuid, userId) {
		const defaultFilename = `user-file-${userId}-${fileUuid}`

		if (!headers) return defaultFilename

		const disposition = headers['content-disposition'] || ''
		const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
		if (utf8Match?.[1]) {
			return decodeURIComponent(utf8Match[1])
		}

		const regularMatch = disposition.match(/filename="?([^";]+)"?/i)
		if (regularMatch?.[1]) {
			return regularMatch[1]
		}

		return defaultFilename
	}

	_toBoolean(value) {
		return value === true || value === 'true' || value === 1 || value === '1'
	}
}

export default new UserService2()
